import React, { useEffect, useRef, useState, Suspense, lazy } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plane, LifeBuoy, Send, FileText, MessageSquareText, SearchCode, Boxes, Square } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SummitLoader } from "@/components/SummitLoader";
import { DiagramViewer } from "@/components/DiagramViewer";

const Aircraft3DViewer = lazy(() => import("@/components/Aircraft3D"));

const AircraftLoader = () => (
  <div className="flex items-center gap-3 py-1" data-testid="ts-loading">
    <SummitLoader size={44} />
    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Consulting IPC…</span>
  </div>
);

// markdown renderer tuned for IPC answers (tables, headings, bold PNs)
const mdComponents = {
  h1: (p) => <h3 className="font-display text-base font-semibold mt-3 mb-1.5 first:mt-0" {...p} />,
  h2: (p) => <h4 className="font-display text-sm font-semibold mt-3 mb-1 first:mt-0" {...p} />,
  h3: (p) => <h4 className="font-display text-sm font-semibold mt-2.5 mb-1 first:mt-0" {...p} />,
  p: (p) => <p className="my-1.5 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 my-1.5 space-y-1" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 my-1.5 space-y-1" {...p} />,
  strong: (p) => <strong className="font-semibold text-summit-primary" {...p} />,
  code: (p) => <code className="font-mono text-[12px] bg-white border border-summit-hairline rounded px-1" {...p} />,
  table: (p) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full" {...p} /></div>,
  th: (p) => <th className="border border-summit-hairline bg-white px-2 py-1 text-left font-mono text-[10px] uppercase tracking-wider text-gray-500" {...p} />,
  td: (p) => <td className="border border-summit-hairline px-2 py-1 align-top" {...p} />,
};

const renderAnswer = (text) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{String(text || "")}</ReactMarkdown>
);

const FUP_MARKER = "===FOLLOWUPS===";
const splitFollowups = (text) => {
  const i = (text || "").indexOf(FUP_MARKER);
  if (i < 0) return { body: (text || "").trim(), followups: [] };
  const followups = text.slice(i + FUP_MARKER.length).split("\n")
    .map((s) => s.replace(/^\s*(?:[-•]|\d+[.)])\s*/, "").trim()).filter(Boolean).slice(0, 3);
  return { body: text.slice(0, i).trim(), followups };
};
// while streaming: hide the followups block and any partially-typed marker tail
const streamDisplay = (text) => {
  const i = (text || "").indexOf(FUP_MARKER);
  const t = i >= 0 ? text.slice(0, i) : text || "";
  return t.replace(/\n={1,3}[A-Z]*={0,3}\s*$/, "").trimEnd();
};

export default function TSHelper() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const hasAccess = isAdmin || !!user?.ts_helper_access;

  const [tails, setTails] = useState([]);
  const [tail, setTail] = useState("");
  const [mode, setMode] = useState("question");
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [thinking, setThinking] = useState(false);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);
  const [diagram, setDiagram] = useState(null);
  const [show3D, setShow3D] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!hasAccess) return;
    api.get("/fleet/ts-helper/tails").then(({ data }) => setTails(data.tails || [])).catch(() => {});
  }, [hasAccess]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  const stop = () => abortRef.current?.abort();

  const ask = async (preset) => {
    const q = (typeof preset === "string" ? preset : question).trim();
    if (!tail) { toast.error("Select a tail number first"); return; }
    if (!q || busy) return;
    setQuestion("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((ms) => [...ms, { role: "user", content: mode === "fault" ? `Fault code: ${q}` : q }]);
    setThinking(true);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let text = "";
    let meta = null;
    const upsert = (final = false, extra = {}) => {
      setMessages((ms) => {
        const c = [...ms];
        const last = c[c.length - 1];
        const msg = { role: "assistant", content: text, streaming: !final, ...extra };
        if (last && last.role === "assistant" && last.streaming) c[c.length - 1] = msg;
        else c.push(msg);
        return c;
      });
    };
    try {
      const res = await fetch(`${API_BASE}/fleet/ts-helper/ask-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ tail, question: q, history, mode }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        let msg = "The IPC assistant is unavailable right now.";
        try { const j = await res.json(); if (j?.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch {}
        throw new Error(msg);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let streamError = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const line = buf.slice(0, i).trim();
          buf = buf.slice(i + 2);
          if (!line.startsWith("data: ")) continue;
          const p = JSON.parse(line.slice(6));
          if (p.t === "meta") meta = p;
          else if (p.t === "d") { text += p.c; setThinking(false); upsert(); }
          else if (p.t === "err") streamError = p.m;
        }
      }
      if (streamError) throw new Error(streamError);
      if (!text) throw new Error("No answer was produced — please try again.");
      {
        const { body, followups } = splitFollowups(text);
        upsert(true, { streaming: false, content: body, followups, diagram: meta?.diagram || null });
      }
    } catch (e) {
      if (e?.name === "AbortError") {
        if (text) {
          const { body, followups } = splitFollowups(text);
          upsert(true, { streaming: false, content: body + "\n\n_Stopped._", followups, diagram: meta?.diagram || null });
        } else {
          setMessages((ms) => [...ms, { role: "assistant", content: "_Stopped._", streaming: false }]);
        }
      } else {
        const msg = e?.message || "The IPC assistant is unavailable right now.";
        if (text) { text = text + "\n\n" + msg; upsert(true, { streaming: false }); }
        else setMessages((ms) => [...ms, { role: "assistant", content: msg, error: true }]);
      }
    } finally { setThinking(false); setBusy(false); abortRef.current = null; }
  };

  if (!hasAccess) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="ts-no-access">
        <LifeBuoy className="w-10 h-10 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">T/S Helper</h1>
        <p className="text-sm text-gray-500 mt-2">Ask an admin to grant you T/S Helper access.</p>
      </div>
    );
  }

  const rj85 = tails.filter((t) => t.fleet === "AVRO RJ85");
  const rj100 = tails.filter((t) => t.fleet === "AVRO RJ100");

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8" data-testid="ts-helper-page">
      {diagram && <DiagramViewer target={diagram} onClose={() => setDiagram(null)} />}
      {show3D && tail && (
        <Suspense fallback={<div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center"><SummitLoader size={72} /></div>}>
          <Aircraft3DViewer tail={tail} fleet={tails.find((t) => t.tail === tail)?.fleet || "AVRO RJ85"} onClose={() => setShow3D(false)} />
        </Suspense>
      )}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Fleet · IPC Troubleshooting</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">T/S Helper</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Left: aircraft + search mode */}
        <div className="w-full lg:w-80 shrink-0 space-y-4 lg:sticky lg:top-20">
          <div className="border border-summit-hairline rounded-lg bg-white" data-testid="ts-tail-panel">
            <div className="px-4 py-3 border-b border-summit-hairline bg-summit-glacier/50">
              <span className="font-display text-sm font-medium tracking-tight flex items-center gap-2">
                <Plane className="w-4 h-4 text-summit-primary" /> Target aircraft
              </span>
            </div>
            <div className="p-4">
              <Select value={tail} onValueChange={setTail}>
                <SelectTrigger data-testid="ts-tail-select"><SelectValue placeholder="Select RJ tail number" /></SelectTrigger>
                <SelectContent>
                  {rj85.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>AVRO RJ85</SelectLabel>
                      {rj85.map((t) => <SelectItem key={t.tail} value={t.tail} data-testid={`ts-tail-${t.tail}`}>{t.tail}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {rj100.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>AVRO RJ100</SelectLabel>
                      {rj100.map((t) => <SelectItem key={t.tail} value={t.tail} data-testid={`ts-tail-${t.tail}`}>{t.tail}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-400 mt-2">Answers are validated against this tail's effectivity.</p>

              <button
                onClick={() => setShow3D(true)}
                disabled={!tail}
                className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-md border border-summit-hairline bg-white px-2 py-2 text-xs font-medium text-summit-primary hover:border-summit-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ts-3d-btn"
              >
                <Boxes className="w-3.5 h-3.5" /> View 3D aircraft
              </button>

              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Search mode</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => setMode("question")}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${mode === "question" ? "border-summit-primary bg-summit-primary text-white" : "border-summit-hairline bg-white text-gray-600 hover:border-gray-300"}`}
                    data-testid="ts-mode-question"
                  >
                    <MessageSquareText className="w-3.5 h-3.5" /> Question
                  </button>
                  <button
                    onClick={() => setMode("fault")}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${mode === "fault" ? "border-summit-primary bg-summit-primary text-white" : "border-summit-hairline bg-white text-gray-600 hover:border-gray-300"}`}
                    data-testid="ts-mode-fault"
                  >
                    <SearchCode className="w-3.5 h-3.5" /> Fault code
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">
                  {mode === "fault" ? "Enter a fault code or warning caption — the AMM fault isolation is searched first." : "Ask anything from the IPC / AMM in plain language."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: chat */}
        <div className="flex-1 w-full border border-summit-hairline rounded-lg bg-white flex flex-col" style={{ minHeight: "60vh" }} data-testid="ts-chat-panel">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" style={{ maxHeight: "65vh" }}>
            {messages.length === 0 && !thinking && (
              <div className="text-center py-16" data-testid="ts-chat-empty">
                <LifeBuoy className="w-10 h-10 mx-auto text-gray-200" />
                <p className="text-sm text-gray-500 mt-3">Select a tail, then ask anything from the IPC / AMM —</p>
                <p className="text-xs text-gray-400 mt-1">"What's the P/N for the fuel LP pump on this tail?" · fault code "32-41-13" · "How do I bleed the brakes?"</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-3 text-sm break-words ${
                    m.role === "user" ? "bg-summit-primary text-white whitespace-pre-wrap"
                      : m.error ? "bg-red-50 text-red-700 border border-red-200 whitespace-pre-wrap"
                      : "bg-summit-glacier/60 border border-summit-hairline text-gray-800"}`}
                  data-testid={`ts-msg-${i}`}
                >
                  {m.role === "assistant" ? renderAnswer(m.streaming ? streamDisplay(m.content) : m.content) : m.content}
                  {m.diagram && (
                    <div className="mt-3 pt-2 border-t border-summit-hairline">
                      <button
                        onClick={() => setDiagram(m.diagram)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-summit-primary border border-summit-hairline bg-white rounded-md px-2.5 py-1.5 hover:border-summit-primary transition-colors"
                        data-testid={`ts-view-diagram-${i}`}
                      >
                        <FileText className="w-3.5 h-3.5" /> View diagram
                      </button>
                    </div>
                  )}
                  {m.followups?.length > 0 && (
                    <div className="mt-3 pt-2.5 border-t border-summit-hairline">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 mb-1.5">Dig deeper</div>
                      <div className="flex flex-wrap gap-1.5">
                        {m.followups.map((f, fi) => (
                          <button
                            key={fi}
                            onClick={() => ask(f)}
                            disabled={busy}
                            className="text-left text-xs text-summit-primary border border-summit-hairline bg-white rounded-full px-3 py-1.5 hover:border-summit-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`ts-followup-${i}-${fi}`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {thinking && <AircraftLoader />}
          </div>
          <div className="border-t border-summit-hairline p-3 sm:p-4">
            <div className="flex gap-2 items-end">
              <Textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!busy) ask(); } }}
                placeholder={!tail ? "Select a tail number first…" : mode === "fault" ? `Enter a fault code for ${tail}…` : `Ask about ${tail}…`}
                className="min-h-[44px] max-h-36 text-sm resize-none"
                rows={1}
                data-testid="ts-question-input"
              />
              {busy ? (
                <Button onClick={stop} variant="outline" className="h-11 px-4 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" title="Stop generating" data-testid="ts-stop-btn">
                  <Square className="w-3.5 h-3.5 fill-current" />
                </Button>
              ) : (
                <Button onClick={() => ask()} disabled={!tail || !question.trim()} className="h-11 px-4" data-testid="ts-ask-btn">
                  <Send className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
